import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Image } from "@/src/lib/components/custom/Image";
import Icon from "../../lib/components/custom/Icon";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import { Input } from "../../lib/components/ui/input";
import { Label } from "../../lib/components/ui/label";
import { Separator } from "../../lib/components/ui/separator";
import { useApi } from "../../lib/hooks/api/use-api";
import Layout from "../layout";

export default function Signin() {
	const { t } = useTranslation();
	const { auth, admins } = useApi();
	const navigate = useNavigate();
	const { data: session } = auth.useSession();
	const { data: adminCheck } = admins.checkCurrentUserIsAdmin();

	useEffect(() => {
		if (session?.user) {
			if (adminCheck?.isAdmin) {
				navigate({ to: "/admin" });
			} else {
				navigate({ to: "/profile" });
			}
		}
	}, [session, adminCheck, navigate]);
	const [isLoading, setIsLoading] = useState(false);
	const [formData, setFormData] = useState({
		email: "",
		password: "",
	});
	const [error, setError] = useState("");

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
		if (error) setError(""); // Clear error on input change
	};

	const handleSignin = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		setIsLoading(true);

		// Basic validation
		if (!formData.email.trim()) {
			setError(t("auth.signin.errors.email_required"));
			setIsLoading(false);
			return;
		}

		if (formData.password.length < 6) {
			setError(t("auth.signin.errors.password_min"));
			setIsLoading(false);
			return;
		}

		try {
			const response = await auth.signIn.email({
				email: formData.email,
				password: formData.password,
			});

			console.log(response);

			if (response.error) {
				setError(
					response.error.message || t("auth.signin.errors.signin_failed"),
				);
			} else {
				console.log("Signin successful:", response.data);
				// Redirect to home after successful signin
				navigate({ to: "/" });
			}
		} catch (err) {
			setError(t("auth.signin.errors.unexpected"));
			console.error("Signin error:", err);
		} finally {
			setIsLoading(false);
		}
	};

	const handleGoogleSignin = async () => {
		try {
			await auth.signIn.social({
				provider: "google",
				callbackURL: `${window.location.origin}/`,
			});
		} catch (err) {
			setError(t("auth.signin.errors.google_failed"));
			console.error("Google signin error:", err);
		}
	};

	return (
		<Layout>
			<div className="p-8 border min-h-full rounded-large bg-gradient-main">
				<div className="max-w-md mx-auto">
					{/* Header */}
					<div className="text-center mb-8">
						<h1 className="text-3xl font-manrope font-semibold mb-2">
							{t("auth.signin.header")}
						</h1>
						<p className="text-muted-foreground">
							{t("auth.signin.subheader")}
						</p>
					</div>

					{/* Signin Form */}
					<Card className="p-6">
						<form onSubmit={handleSignin} className="space-y-4">
							{/* Email Field */}
							<div>
								<Label htmlFor="email">{t("auth.signin.email_label")}</Label>
								<Input
									id="email"
									name="email"
									type="email"
									placeholder={t("auth.signin.email_placeholder")}
									value={formData.email}
									onChange={handleInputChange}
									required
								/>
							</div>

							{/* Password Field */}
							<div>
								<Label htmlFor="password">
									{t("auth.signin.password_label")}
								</Label>
								<Input
									id="password"
									name="password"
									type="password"
									placeholder={t("auth.signin.password_placeholder")}
									value={formData.password}
									onChange={handleInputChange}
									required
								/>
								{/* Forgot Password Link */}
								<div className="text-right mt-2">
									<Link
										to="/auth/forgot-password"
										className="text-sm text-primary hover:underline"
									>
										{t("auth.signin.forgot_password")}
									</Link>
								</div>
							</div>

							{/* Error Message */}
							{error && (
								<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
									{error}
								</div>
							)}

							{/* Signin Button */}
							<Button
								variant="primary"
								type="submit"
								className="w-full"
								disabled={isLoading}
							>
								{isLoading ? (
									<>
										<Icon name="Loader" className="w-4 h-4 mr-2 animate-spin" />
										{t("auth.signin.signing_in")}
									</>
								) : (
									<>
										<Icon name="LogIn" className="w-4 h-4 mr-2" />
										{t("auth.signin.submit")}
									</>
								)}
							</Button>
						</form>

						{/* Divider */}
						<div className="relative my-6">
							<Separator />
							<div className="absolute inset-0 flex items-center justify-center">
								<span className="bg-card px-2 text-xs text-muted-foreground">
									{t("auth.signin.or")}
								</span>
							</div>
						</div>

						{/* Google Signin */}
						<Button
							variant="default"
							className="w-full"
							onClick={handleGoogleSignin}
							disabled={isLoading}
						>
							<Image
								src="/static/icons/google.webp"
								alt="Google"
								width={20}
								height={20}
							/>
							{t("auth.signin.google")}
						</Button>
					</Card>

					{/* Signup Link */}
					<div className="text-center mt-6">
						<p className="text-muted-foreground">
							{t("auth.signin.no_account")}{" "}
							<Link
								to="/auth/signup"
								className="text-primary font-medium hover:underline"
							>
								{t("auth.signin.signup_link")}
							</Link>
						</p>
					</div>

					{/* Back to Home */}
					<div className="text-center mt-4">
						<Button variant="ghost" asChild>
							<Link to="/">
								<Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
								{t("auth.signin.back_to_home")}
							</Link>
						</Button>
					</div>
				</div>
			</div>
		</Layout>
	);
}
